import React, { useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';

function normalizeHex(input) {
  if (!input || input === 'transparent' || input === 'none') return null;
  let s = String(input).trim();
  if (/^rgba?\(/i.test(s)) return s;
  if (!s.startsWith('#')) s = `#${s}`;
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    const h = s.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  return null;
}

/** Color field using react-colorful (HexColorPicker) + hex input + optional swatches. */
export default function ColorPickerField({
  label,
  value,
  onChange,
  swatches = [],
  allowTransparent = false,
  className = '',
  compact = false,
}) {
  const hex = normalizeHex(value) || '#000000';
  const isTransparent = value === 'transparent' || value === 'none' || value === '';
  const [hexDraft, setHexDraft] = useState(hex);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const n = normalizeHex(value);
    if (n) setHexDraft(n);
    else if (isTransparent) setHexDraft('');
  }, [value, isTransparent]);

  const pickerColor = isTransparent ? '#ffffff' : hex;

  return (
    <div className={`space-y-1.5 ${className}`}>
      {label ? <span className="text-[10px] font-medium text-slate-500">{label}</span> : null}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="relative h-10 w-10 shrink-0 rounded-lg border border-slate-200 shadow-sm overflow-hidden ring-offset-1 hover:ring-2 hover:ring-indigo-200 transition-shadow"
          title="Pick color"
          aria-expanded={open}
        >
          <span
            className="absolute inset-0 block"
            style={{
              background: isTransparent
                ? 'repeating-conic-gradient(#e2e8f0 0% 25%, #fff 0% 50%) 50% / 10px 10px'
                : pickerColor,
            }}
          />
        </button>
        <input
          type="text"
          value={isTransparent ? '' : hexDraft}
          placeholder={allowTransparent ? 'transparent' : '#000000'}
          onChange={(e) => {
            const v = e.target.value.trim();
            setHexDraft(v);
            const n = normalizeHex(v);
            if (n) onChange(n);
            else if (allowTransparent && (v === '' || v === 'transparent')) onChange('transparent');
          }}
          onBlur={() => {
            const n = normalizeHex(hexDraft);
            if (n) onChange(n);
          }}
          className="flex-1 min-w-[5.5rem] rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-mono text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
        />
        {allowTransparent && (
          <button
            type="button"
            onClick={() => onChange('transparent')}
            className={`text-[10px] font-semibold px-2 py-1.5 rounded-md border ${
              isTransparent ? 'border-indigo-400 bg-indigo-50 text-indigo-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            None
          </button>
        )}
      </div>
      {open && !isTransparent && (
        <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-md">
          <HexColorPicker
            color={pickerColor}
            onChange={(c) => {
              onChange(c);
              setHexDraft(c);
            }}
            style={{ width: '100%', height: compact ? 100 : 140 }}
          />
        </div>
      )}
      {swatches.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {swatches.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              onClick={() => onChange(c)}
              className={`w-7 h-7 rounded-md border-2 transition-all ${
                normalizeHex(value) === normalizeHex(c) ? 'border-indigo-500 ring-1 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
